import { inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { AlertService } from '@nx-vigil/core/web/alert/data-access';
import { LoggingService } from '@nx-vigil/core/web/logging/data-access';
import { firstValueFrom, Observable } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';

export const enum LoadingState {
  INIT = 'INIT',
  LOADING = 'LOADING',
  LOADED = 'LOADED',
}
export interface ErrorState {
  errorMsg: string;
}

export type CallState = LoadingState | ErrorState;

export function getError(callState: CallState): string | null {
  if ((callState as ErrorState).errorMsg !== undefined) {
    return (callState as ErrorState).errorMsg;
  }
  return null;
}

export class LoadableStore<T extends object> extends ComponentStore<
  T & { callState: CallState }
> {
  protected logger: LoggingService = inject(LoggingService);
  protected alertService: AlertService = inject(AlertService);
  public constructor(initialState: T) {
    super({
      ...initialState,
      callState: LoadingState.INIT,
    });
  }

  public readonly loading$ = this.select(
    ({ callState }) => callState === LoadingState.LOADING,
  );
  public readonly onceLoading$ = this.loading$.pipe(
    skipWhile(loading => !loading),
    take(1),
  );

  public readonly loaded$ = this.select(
    ({ callState }) => callState === LoadingState.LOADED,
  );
  public readonly onceLoaded$ = this.loaded$.pipe(
    skipWhile(loaded => !loaded),
    take(1),
  );

  public readonly error$ = this.select(({ callState }) => callState).pipe(
    skipWhile(callState => callState === LoadingState.INIT),
    skipWhile(callState => callState === LoadingState.LOADING),
    map(callState => getError(callState)),
  );

  public readonly setLoading = this.updater((state: T) => ({
    ...state,
    callState: LoadingState.LOADING,
  }));

  public readonly setLoaded = this.updater((state: T) => ({
    ...state,
    callState: LoadingState.LOADED,
  }));

  public setError(errorMsg: string, error?: Error) {
    const resolvedMsg = this.alertService.error(errorMsg, error);
    this.updater((state: T) => ({
      ...state,
      callState: {
        errorMsg: resolvedMsg,
      },
    }))();
  }

  public setSuccess(successMsg: string) {
    this.alertService.success(successMsg);
  }

  public async waitOnce() {
    const error = await firstValueFrom(this.error$);
    if (error) throw new Error(error);
  }

  public async waitForFirstFrom<TResult>(execute$: Observable<TResult>) {
    await this.waitOnce();
    return firstValueFrom(execute$);
  }
}
